import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { Target, CheckCircle, XCircle, RefreshCw, LogIn } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("");
  const [username, setUsername] = useState("");

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      setMessage("No verification token found.");
      return;
    }
    axios.get(`${API}/auth/verify?token=${token}`)
      .then((res) => {
        setStatus("success");
        setMessage(res.data.message);
        setUsername(res.data.username || "");
      })
      .catch((e) => {
        setStatus("error");
        setMessage(e.response?.data?.detail || "Verification failed.");
      });
  }, [searchParams]);

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] relative"
      style={{
        backgroundImage: "url(https://static.prod-images.emergentagent.com/jobs/f707a822-bbe1-4c69-96b7-9259b8de4d00/images/fbdf8f044500ad2fe4d88a49bf647ff10fc9a081bcebea33ee50a27fb0f33e44.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-black/80" />
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <Target className="w-12 h-12 text-[#E3292E] mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-display text-3xl text-white uppercase tracking-tighter">Email Verification</h1>
          </div>

          {status === "verifying" && (
            <Card className="bg-[#141414]/90 backdrop-blur-sm border-white/10 rounded-none">
              <CardContent className="py-12 text-center space-y-4">
                <RefreshCw className="w-10 h-10 text-[#E3292E] animate-spin mx-auto" />
                <p className="text-white/50 font-body">Verifying your email...</p>
              </CardContent>
            </Card>
          )}

          {status === "success" && (
            <Card className="bg-[#141414]/90 backdrop-blur-sm border-white/10 rounded-none" data-testid="verify-success-card">
              <CardContent className="py-8 space-y-6">
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 text-[#309F6A] mx-auto mb-4" />
                  <h2 className="font-heading font-bold text-white text-xl mb-2">Thank You!</h2>
                  <p className="text-white/60 font-body text-sm">{message}</p>
                </div>

                <div className="bg-white/5 border border-white/10 p-4 space-y-2">
                  <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-bold">Your Login Details</p>
                  {username && (
                    <p className="text-white text-sm font-body">
                      <span className="text-white/50">Username:</span> <strong>{username}</strong>
                    </p>
                  )}
                  <p className="text-white/40 text-xs font-body">Use the password you set during registration.</p>
                </div>

                <p className="text-white/40 text-xs text-center font-body">
                  A confirmation email has also been sent to your inbox.
                </p>

                <Button
                  onClick={() => navigate("/admin")}
                  className="w-full bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none py-5 font-heading font-bold"
                  data-testid="go-to-login-btn"
                >
                  <LogIn className="mr-2 h-4 w-4" /> LOG IN NOW
                </Button>
              </CardContent>
            </Card>
          )}

          {status === "error" && (
            <Card className="bg-[#141414]/90 backdrop-blur-sm border-white/10 rounded-none" data-testid="verify-error-card">
              <CardContent className="py-8 text-center space-y-6">
                <XCircle className="w-16 h-16 text-[#E3292E] mx-auto" />
                <div>
                  <h2 className="font-heading font-bold text-white text-xl mb-2">Verification Failed</h2>
                  <p className="text-white/60 font-body text-sm">{message}</p>
                </div>
                <Button
                  onClick={() => navigate("/admin")}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10 rounded-none py-5 font-heading"
                  data-testid="back-to-login-btn"
                >
                  Back to Login
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
